import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Eye, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    meta_title: '',
    meta_description: '',
    featured_image: '',
    is_published: false
  });
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (isEditing) {
      fetchPost();
    }
  }, [id, navigate, isEditing]);

  const fetchPost = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/blog/posts/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch post');
      const data = await response.json();
      setFormData({
        title: data.title || '',
        slug: data.slug || '',
        excerpt: data.excerpt || '',
        content: data.content || '',
        meta_title: data.meta_title || '',
        meta_description: data.meta_description || '',
        featured_image: data.featured_image || '',
        is_published: data.is_published || false
      });
    } catch (err) {
      // Error handled by UI state
      alert('Failed to load post');
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue,
      // Auto-generate slug from title if creating new post
      ...(name === 'title' && !isEditing && !formData.slug ? { slug: generateSlug(value) } : {})
    }));

    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.content.trim()) newErrors.content = 'Content is required';
    if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = isEditing 
        ? `${API_URL}/api/admin/blog/posts/${id}`
        : `${API_URL}/api/admin/blog/posts`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          meta_title: formData.meta_title || formData.title,
          meta_description: formData.meta_description || formData.excerpt
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save post');
      }

      navigate('/admin/blog');
    } catch (err) {
      // Error handled by UI state
      setErrors(prev => ({ ...prev, submit: err.message }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white" data-testid="admin-blog-editor">
      {/* Header */}
      <header className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/blog')}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="font-heading text-xl font-bold">
                  {isEditing ? 'Edit Post' : 'New Post'}
                </h1>
                {formData.slug && (
                  <p className="text-sm text-slate-500 font-mono">/blog/{formData.slug}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {formData.is_published && formData.slug && (
                <a
                  href={`/blog/${formData.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  <span className="hidden sm:inline">Preview</span>
                </a>
              )}
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-6 font-bold shadow-[0_0_20px_rgba(16,185,129,0.3)]"
                data-testid="save-post-btn"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {errors.submit && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3 text-red-400"
          >
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p>{errors.submit}</p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Main Content Section */}
          <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8">
            <h2 className="font-heading text-lg font-bold text-white mb-6">Content</h2>
            
            <div className="space-y-6">
              {/* Title */}
              <div>
                <Label htmlFor="title" className="text-slate-300 mb-2 block">
                  Title <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter post title"
                  className={`bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 text-lg ${errors.title ? 'border-red-500' : ''}`}
                  data-testid="post-title-input"
                />
                {errors.title && <p className="text-red-400 text-sm mt-1">{errors.title}</p>}
              </div>

              {/* Slug */}
              <div>
                <Label htmlFor="slug" className="text-slate-300 mb-2 block">
                  URL Slug <span className="text-red-400">*</span>
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 text-sm">/blog/</span>
                  <Input
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    placeholder="url-friendly-slug"
                    className={`bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 font-mono ${errors.slug ? 'border-red-500' : ''}`}
                    data-testid="post-slug-input"
                  />
                </div>
                {errors.slug && <p className="text-red-400 text-sm mt-1">{errors.slug}</p>}
              </div>

              {/* Excerpt */}
              <div>
                <Label htmlFor="excerpt" className="text-slate-300 mb-2 block">
                  Excerpt
                </Label>
                <textarea
                  id="excerpt"
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleChange}
                  placeholder="Brief summary shown in blog listings..."
                  rows={3}
                  className="w-full bg-slate-800/50 border border-slate-700 text-white placeholder:text-slate-500 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                  data-testid="post-excerpt-input"
                />
              </div>

              {/* Content */}
              <div>
                <Label htmlFor="content" className="text-slate-300 mb-2 block">
                  Content (Markdown) <span className="text-red-400">*</span>
                </Label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  placeholder="Write your blog post content in Markdown format..."
                  rows={20}
                  className={`w-full bg-slate-800/50 border border-slate-700 text-white placeholder:text-slate-500 rounded-xl px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 ${errors.content ? 'border-red-500' : ''}`}
                  data-testid="post-content-input"
                />
                {errors.content && <p className="text-red-400 text-sm mt-1">{errors.content}</p>}
                <p className="text-slate-500 text-sm mt-2">
                  Supports Markdown: **bold**, *italic*, ## headings, - lists, [links](url), {'>'} quotes
                </p>
              </div>
            </div>
          </div>

          {/* SEO Section */}
          <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8">
            <h2 className="font-heading text-lg font-bold text-white mb-6">SEO & Meta</h2>
            
            <div className="space-y-6">
              {/* Meta Title */}
              <div>
                <Label htmlFor="meta_title" className="text-slate-300 mb-2 block">
                  Meta Title
                </Label>
                <Input
                  id="meta_title"
                  name="meta_title"
                  value={formData.meta_title}
                  onChange={handleChange}
                  placeholder="SEO title (defaults to post title)"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="post-meta-title-input"
                />
                <p className="text-slate-500 text-sm mt-1">
                  {formData.meta_title.length || formData.title.length}/60 characters
                </p>
              </div>

              {/* Meta Description */}
              <div>
                <Label htmlFor="meta_description" className="text-slate-300 mb-2 block">
                  Meta Description
                </Label>
                <textarea
                  id="meta_description"
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleChange}
                  placeholder="SEO description (defaults to excerpt)"
                  rows={2}
                  className="w-full bg-slate-800/50 border border-slate-700 text-white placeholder:text-slate-500 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                  data-testid="post-meta-desc-input"
                />
                <p className="text-slate-500 text-sm mt-1">
                  {formData.meta_description.length || formData.excerpt.length}/160 characters
                </p>
              </div>

              {/* Featured Image */}
              <div>
                <Label htmlFor="featured_image" className="text-slate-300 mb-2 block">
                  Featured Image URL
                </Label>
                <Input
                  id="featured_image"
                  name="featured_image"
                  value={formData.featured_image}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="post-image-input"
                />
              </div>
            </div>
          </div>

          {/* Publishing Section */}
          <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8">
            <h2 className="font-heading text-lg font-bold text-white mb-6">Publishing</h2>
            
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="is_published"
                checked={formData.is_published}
                onChange={handleChange}
                className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-900"
                data-testid="post-published-checkbox"
              />
              <div>
                <span className="text-white font-medium">Publish this post</span>
                <p className="text-slate-500 text-sm">
                  {formData.is_published ? 'This post will be visible on the blog' : 'Save as draft'}
                </p>
              </div>
            </label>
          </div>

          {/* Submit Button (Mobile) */}
          <div className="sm:hidden">
            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full py-6 font-bold text-lg"
            >
              <Save className="mr-2 h-5 w-5" />
              {saving ? 'Saving...' : (isEditing ? 'Update Post' : 'Create Post')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminBlogEditor;
